
import { useState } from 'react';
import { executeMultiAgentTask } from './orchestration/taskExecutor';
import { getActivePlans } from '@/utils/a2a/orchestration/adaptive';
import { toast } from 'sonner';
import { extractParticipants, extractLocation, extractTimeInfo } from './useA2AContextExtractors';
import { requiresExternalApproval, needsContextualClarification } from './useA2AClarification';

/**
 * Hook for managing multi-agent task execution
 */
export const useRunMultiAgentTask = ({
  setIsProcessing,
  setOrchestrationPlans,
  setAgentResponses,
  setPeerMessages,
  setPendingMessages,
  requestApproval,
  updateOrchestrationPlans
}: any) => {
  const runMultiAgentTask = async (
    task: string, 
    capabilities: string[], 
    complexity: 'simple' | 'medium' | 'complex' = 'medium',
    sendMessage: any
  ) => {
    try {
      setIsProcessing(true);

      // Extract and update context
      const extractedContext = {
        participants: extractParticipants(task),
        location: extractLocation(task),
        time: extractTimeInfo(task)
      };

      // Check if we need clarification
      const clarificationNeeded = needsContextualClarification(task, extractedContext);
      if (clarificationNeeded) {
        const clarificationMessage = {
          id: Date.now().toString(),
          content: clarificationNeeded,
          sender: 'ai',
          timestamp: new Date()
        };
        setAgentResponses((prev: any) => ({
          ...prev,
          [task]: clarificationMessage.content
        }));
        setIsProcessing(false);
        return clarificationMessage;
      }

      // If it's an external task that requires approval
      if (requiresExternalApproval(task)) {
        const approved = await requestApproval(
          `Approve external communication for task: ${task}`
        );
        if (!approved) {
          toast.info("Task execution cancelled by user");
          setIsProcessing(false);
          return null;
        }
      }

      const messages = await executeMultiAgentTask(task, capabilities, complexity, sendMessage, {
        updatePlans: updateOrchestrationPlans
      });

      setPendingMessages((prev: any[]) => [...prev, ...messages]);
      setOrchestrationPlans(getActivePlans());
      
      setTimeout(() => {
        setIsProcessing(false);
      }, 2000);

      return messages;
    } catch (error) {
      console.error("Error in orchestration:", error);
      toast.error("Failed to process the request");
      setIsProcessing(false);
      return null;
    }
  };

  return { runMultiAgentTask };
};
